import { z } from 'zod';
import { Confidence, entity, IdSchema, IsoDateSchema, IsoDateTimeSchema } from './common';

export const SessionStatusSchema = z.enum(['draft', 'processing', 'verification', 'ai_pending', 'review', 'approved']);
export type SessionStatus = z.infer<typeof SessionStatusSchema>;

export const ProcessingStateSchema = z
  .object({
    filesSaved: z.boolean(),
    samsungParsed: z.boolean(),
    appleParsed: z.boolean(),
    transcriptsAligned: z.boolean(),
    rolesInferred: z.boolean(),
    discrepanciesDetected: z.boolean(),
    workingTranscriptCreated: z.boolean(),
    verificationComplete: z.boolean(),
    aiPacketCreated: z.boolean(),
    aiAnalysisImported: z.boolean(),
    journalApproved: z.boolean(),
    nextSessionBriefCreated: z.boolean(),
    synced: z.boolean(),
    /** 재개용 마지막 오류. 내용 원문 금지(코드만). */
    lastError: z.object({ step: z.string(), code: z.string(), at: IsoDateTimeSchema }).strict().optional(),
  })
  .strict();
export type ProcessingState = z.infer<typeof ProcessingStateSchema>;

export const SessionSchema = entity('Session', {
  clientId: IdSchema,
  sessionNumber: z.number().int().min(1),
  sessionDate: IsoDateSchema,
  /** 상한 없음 (MASTER §3A) */
  durationMinutes: z.number().int().min(1).optional(),
  modality: z.enum(['individual', 'couple', 'family', 'group']),
  /** Amendment A25: 상담 장소 */
  location: z.string().max(100).optional(),
  participantIds: z.array(IdSchema).optional(),

  status: SessionStatusSchema,

  primaryThemes: z.array(z.string()).optional(),
  sessionFocus: z.string().optional(),

  counselorMemoIds: z.array(IdSchema).optional(),

  samsungTranscriptId: IdSchema.optional(),
  appleTranscriptId: IdSchema.optional(),
  workingTranscriptId: IdSchema.optional(),

  verifiedEvidenceIds: z.array(IdSchema).optional(),
  audioRefs: z.array(IdSchema).optional(),

  processingState: ProcessingStateSchema,
});
export type Session = z.infer<typeof SessionSchema>;

// ---------- Transcript (Amendment A1) ----------

export const ClinicalRoleSchema = z.enum(['counselor', 'client', 'unknown', 'mixed']);
export type ClinicalRole = z.infer<typeof ClinicalRoleSchema>;

export const CounselorActSchema = z.enum([
  'question',
  'reflection',
  'restatement',
  'interpretation',
  'self-disclosure',
  'validation',
  'information',
  'structuring',
  'backchannel',
  'other',
]);

export const RawRefSchema = z
  .object({
    transcriptId: IdSchema,
    lineStart: z.number().int().min(1),
    lineEnd: z.number().int().min(1),
    charStart: z.number().int().min(0).optional(),
    charEnd: z.number().int().min(0).optional(),
  })
  .strict();

export const TranscriptSegmentSchema = z
  .object({
    segmentId: z.string().min(1),

    startMs: z.number().int().min(0).optional(),
    endMs: z.number().int().min(0).optional(),
    galaxyTimeMs: z.number().int().min(0).optional(),
    iphoneTimeRange: z
      .object({ startMs: z.number().int().min(0), endMs: z.number().int().min(0) })
      .strict()
      .optional(),

    /** 기기 원 라벨 ("발화자 2"). 임상 역할이 아니다. */
    sourceSpeakerLabel: z.string().optional(),
    clinicalRole: ClinicalRoleSchema,
    roleConfidence: Confidence,
    roleSource: z.enum(['auto', 'ai-review', 'user']),
    roleModelVersion: z.string().optional(),
    counselorAct: CounselorActSchema.optional(),
    isBackchannel: z.boolean(),
    localCorrection: z.boolean().optional(),
    splitFrom: z
      .object({ segmentId: z.string(), charStart: z.number().int().min(0), charEnd: z.number().int().min(0) })
      .strict()
      .optional(),

    text: z.string(),
    textConfidence: Confidence.optional(),
    matchScore: Confidence.optional(),

    samsungRawRefs: z.array(RawRefSchema).optional(),
    iphoneRawRefs: z.array(RawRefSchema).optional(),

    discrepancyLevel: z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3), z.literal(4)]).optional(),
    verificationStatus: z.enum(['unreviewed', 'auto-matched', 'needs-review', 'verified', 'ignored']),
    clinicalPriority: z.enum(['normal', 'important', 'critical']).optional(),
    discrepancyIds: z.array(IdSchema).optional(),
    /** Amendment A21: 상담자 해석에 대한 짧은 동의 (T3) — CLIENT_REPORT 근거로 승격 금지 */
    weakAgreement: z.boolean().optional(),
    /** Amendment A21: 용어 사전으로 보정된 용어 (Working 텍스트에만 적용) */
    glossaryCorrected: z.array(z.string()).optional(),
  })
  .strict();
export type TranscriptSegment = z.infer<typeof TranscriptSegmentSchema>;

export const TranscriptSourceSchema = z.enum(['samsung_raw', 'apple_raw', 'working', 'verified']);

export const TranscriptSchema = entity('Transcript', {
  clientId: IdSchema,
  sessionId: IdSchema,
  source: TranscriptSourceSchema,
  originalFormat: z.string().optional(),
  language: z.string().min(2),
  rawBlobId: z.string().optional(),
  parserVersion: z.string().optional(),
  parseStatus: z.enum(['pending', 'parsed', 'partial', 'failed']).optional(),
  parseWarnings: z.array(z.string()).optional(),
  /** Amendment A21: 처리 요약 (개수만, 내용 없음) */
  stats: z
    .object({
      galaxyTurns: z.number().int(),
      iphoneBlocks: z.number().int(),
      aligned: z.number().int(),
      offsetMs: z.number().nullable(),
      needsReview: z.number().int(),
      critical: z.number().int(),
    })
    .strict()
    .optional(),
  segments: z.array(TranscriptSegmentSchema),
});
export type Transcript = z.infer<typeof TranscriptSchema>;

export const TranscriptDiscrepancySchema = entity('TranscriptDiscrepancy', {
  clientId: IdSchema,
  sessionId: IdSchema,

  samsungSegmentIds: z.array(z.string()),
  appleSegmentIds: z.array(z.string()),
  samsungText: z.string(),
  appleText: z.string(),

  normalizedSimilarity: Confidence.optional(),
  level: z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
  category: z.enum([
    'wording',
    'negation',
    'number',
    'person',
    'medication',
    'diagnosis',
    'risk',
    'event',
    'terminology',
    'unknown',
  ]),
  priority: z.enum(['low', 'medium', 'high', 'critical']),
  glossaryTermIds: z.array(IdSchema).optional(),

  resolution: z.enum(['unresolved', 'samsung', 'apple', 'manual', 'ignored']),
  resolvedText: z.string().optional(),
  resolvedBy: z.string().optional(),
  resolvedAt: IsoDateTimeSchema.optional(),
});
export type TranscriptDiscrepancy = z.infer<typeof TranscriptDiscrepancySchema>;

export const CounselorMemoSchema = entity('CounselorMemo', {
  clientId: IdSchema,
  sessionId: IdSchema,
  text: z.string(),
  inputType: z.enum(['text', 'voice-transcribed']),
  evidenceType: z.literal('COUNSELOR_OBSERVATION'),
});
export type CounselorMemo = z.infer<typeof CounselorMemoSchema>;
